import * as z from 'zod';
import axios from 'axios';
import client from '../client';

import { SEO, Localization } from '../strapiSchema';
import { blocksSchema } from '../blocksSchema';
import { blocks, seo, localizations } from '../strapiBlocks';

const PRIMARY_LOCALE = 'fr-FR';

const PageZod = z.object({
  id: z.number(),
  documentId: z.string(),
  slug: z.string(),
  headerColor: z.enum(['noir', 'gris']),
  blocks: z.array(blocksSchema),
  seo: SEO,
  localizations: z.array(Localization),
  locale: z.string(),
});

export type PageType = z.infer<typeof PageZod>;

const PageSlugZod = PageZod.pick({
  id: true,
  documentId: true,
  slug: true,
  seo: true
});

export type PageSlugType = z.infer<typeof PageSlugZod>;

const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const logMissingPage = (slug: string, locale: string) => {
  // eslint-disable-next-line no-console
  console.warn(`[pages] Page "${slug}" not found for locale "${locale}", skipping.`);
};

const logMissingPagesForLocale = (locale: string) => {
  // eslint-disable-next-line no-console
  console.warn(`[pages] No pages found for locale "${locale}", skipping.`);
};

const fetchPageFromApi = async (
  slug: string,
  locale: string,
): Promise<PageType | null> => {
  try {
    const response = await client.get('/api/pages', {
      params: {
        locale,
        populate: {
          blocks,
          seo,
          localizations,
        },
        filters: {
          slug: {
            $eq: slug,
          },
        },
      },
    });

    if (!response.data?.data?.length) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingPage(slug, locale);
      }
      return null;
    }

    return PageZod.parseAsync(response.data.data[0]);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingPage(slug, locale);
      return null;
    }

    throw error;
  }
};

const fetchPagesFromApi = async (locale: string): Promise<unknown[]> => {
  try {
    const pageSize = 50;
    const response = await client.get('/api/pages', {
      params: {
        locale,
        fields: ['slug'],
        populate: {
          seo,
        },
        pagination: {
          page: 1,
          pageSize,
        },
      },
    });

    const totalPages = response.data?.meta?.pagination.pageCount || 1;
    const allData = [...(response.data?.data ?? [])];

    if (totalPages > 1) {
      const pagePromises = Array.from({ length: totalPages - 1 }, (_, i) =>
        client.get('/api/pages', {
          params: {
            locale,
            fields: ['slug'],
            populate: {
              seo,
            },
            pagination: { page: i + 2, pageSize },
          },
        }).catch((error) => {
          if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
            logMissingPagesForLocale(locale);
            return { data: { data: [] } };
          }
          throw error;
        }),
      );
      const results = await Promise.all(pagePromises);
      results.forEach((r) => allData.push(...(r.data?.data || [])));
    }

    return allData;
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingPagesForLocale(locale);
      return [];
    }

    throw error;
  }
};

const getPage = async (
  slug: string,
  locale: string = PRIMARY_LOCALE,
): Promise<PageType | null> => {
  try {
    const page = await fetchPageFromApi(slug, locale);

    if (!page) {
      if (locale !== PRIMARY_LOCALE) {
        return null;
      }
      throw new Error('No page found for this slug');
    }

    return page;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match Page structure : ${errorMessage}`);
  }
};

const getPages = async (
  locale: string = PRIMARY_LOCALE,
): Promise<Array<PageSlugType>> => {
  try {
    return z
      .array(PageSlugZod)
      .parseAsync(await fetchPagesFromApi(locale));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match Page structure : ${errorMessage}`);
  }
};


export {
  getPage,
  getPages
};